<template>
  <div class="signature-pad-container">
    <div class="form-group basic">
      <label class="label" for="signature">
        {{ label || 'Signature' }} 
        <span class="mal-text" v-if="malayalamLabel">({{ malayalamLabel }})</span>
        <span class="text-danger" v-if="required">*</span>
      </label>
      
      <div class="signature-wrapper" :class="{ 'error': hasError }">
        <canvas
          ref="signatureCanvas"
          :width="canvasWidth"
          :height="canvasHeight"
          class="signature-canvas"
          :class="{ 'disabled': disabled }"
          @mousedown="!disabled && startDrawing($event)"
          @mousemove="!disabled && draw($event)"
          @mouseup="!disabled && stopDrawing($event)"
          @mouseleave="!disabled && stopDrawing($event)"
          @touchstart="!disabled && startDrawing($event)"
          @touchmove="!disabled && draw($event)"
          @touchend="!disabled && stopDrawing($event)"
        ></canvas>
        
        <div class="signature-actions">
          <button 
            type="button" 
            class="btn btn-outline-secondary btn-sm me-1" 
            @click="clearSignature"
            :disabled="disabled || loading"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            <ion-icon name="refresh-outline" v-if="!loading"></ion-icon>
            Clear
          </button>
          
          <button 
            type="button" 
            class="btn btn-outline-primary btn-sm" 
            @click="saveSignature"
            :disabled="isEmpty || disabled || loading"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            <ion-icon name="checkmark-outline" v-if="!loading"></ion-icon>
            {{ loading ? 'Saving...' : 'Save' }}
          </button>
        </div>
        
        <div class="signature-preview" v-if="signatureDataUrl && !isDrawing">
          <img :src="signatureDataUrl" alt="Signature" class="signature-image" />
        </div>
      </div>
      
      <div v-if="hasError" class="error-feedback">
        {{ errorMessage }}
      </div>
      
      <div class="signature-instructions">
        <small class="text-muted mal-text">
          മുകളിലുള്ള ബോക്സിൽ നിങ്ങളുടെ ഒപ്പ് വരയ്ക്കുക. മൗസ് അല്ലെങ്കിൽ ടച്ച് ഉപയോഗിച്ച് ഒപ്പിടുക.
        </small>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SignaturePad',
  props: {
    label: {
      type: String,
      default: 'Signature'
    },
    malayalamLabel: {
      type: String,
      default: 'ഒപ്പ്'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    canvasWidth: {
      type: Number,
      default: 350
    },
    canvasHeight: {
      type: Number,
      default: 150
    },
    strokeColor: {
      type: String,
      default: '#000000'
    },
    strokeWidth: {
      type: Number,
      default: 2
    },
    backgroundColor: {
      type: String,
      default: '#ffffff'
    },
    modelValue: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue', 'signature-saved', 'signature-cleared'],
  data() {
    return {
      isDrawing: false,
      isEmpty: true,
      lastX: 0,
      lastY: 0,
      signatureDataUrl: null,
      hasError: false,
      errorMessage: '',
      loading: false
    }
  },
  mounted() {
    this.initializeCanvas();
    if (this.modelValue) {
      this.loadSignature(this.modelValue);
    }
  },
  watch: {
    modelValue(newValue) {
      if (newValue && newValue !== this.signatureDataUrl) {
        this.loadSignature(newValue);
      }
    }
  },
  methods: {
    initializeCanvas() {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      
      // Set canvas background
      ctx.fillStyle = this.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Set drawing styles
      ctx.strokeStyle = this.strokeColor;
      ctx.lineWidth = this.strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    },
    
    getEventPos(event) {
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      let clientX, clientY;
      
      if (event.touches && event.touches.length > 0) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
        event.preventDefault(); // Prevent scrolling
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY
      };
    },
    
    startDrawing(event) {
      this.isDrawing = true;
      const pos = this.getEventPos(event);
      this.lastX = pos.x;
      this.lastY = pos.y;
      this.hasError = false;
    },
    
    draw(event) {
      if (!this.isDrawing) return;
      
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      const pos = this.getEventPos(event);
      
      ctx.beginPath();
      ctx.moveTo(this.lastX, this.lastY);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      
      this.lastX = pos.x;
      this.lastY = pos.y;
      this.isEmpty = false;
    },
    
    stopDrawing() {
      if (this.isDrawing) {
        this.isDrawing = false;
        this.updateSignatureData();
      }
    },
    
    updateSignatureData() {
      const canvas = this.$refs.signatureCanvas;
      this.signatureDataUrl = canvas.toDataURL('image/png');
      this.$emit('update:modelValue', this.signatureDataUrl);
    },
    
    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      
      // Clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Reset background
      ctx.fillStyle = this.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      this.isEmpty = true;
      this.signatureDataUrl = null;
      this.hasError = false;
      
      this.$emit('update:modelValue', null);
      this.$emit('signature-cleared');
    },
    
    saveSignature() {
      if (this.isEmpty) {
        this.showError('Please provide a signature');
        return;
      }
      
      this.updateSignatureData();
      this.$emit('signature-saved', this.signatureDataUrl);
    },
    
    loadSignature(dataUrl) {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = this.backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        this.isEmpty = false;
        this.signatureDataUrl = dataUrl;
      };
      
      img.src = dataUrl;
    },
    
    validateSignature() {
      if (this.required && this.isEmpty) {
        this.showError('Signature is required');
        return false;
      }
      this.hasError = false;
      return true;
    },
    
    showError(message) {
      this.hasError = true;
      this.errorMessage = message;
    },
    
    // Public method to get signature data
    getSignatureData() {
      return this.signatureDataUrl;
    },
    
    // Public method to check if signature is empty
    isSignatureEmpty() {
      return this.isEmpty;
    }
  }
}
</script>

<style scoped>
.signature-pad-container {
  margin-bottom: 1rem;
}

.signature-wrapper {
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  padding: 15px;
  background: #ffffff;
  position: relative;
}

.signature-wrapper.error {
  border-color: #dc3545;
}

.signature-canvas {
  border: 1px dashed #dee2e6;
  border-radius: 4px;
  display: block;
  margin: 0 auto;
  cursor: crosshair;
  background: #ffffff;
  touch-action: none;
  max-width: 100%;
}

.signature-canvas.disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background: #f8f9fa;
}

.signature-actions {
  margin-top: 10px;
  text-align: center;
}

.signature-preview {
  margin-top: 15px;
  text-align: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
}

.signature-image {
  max-width: 100%;
  height: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.signature-instructions {
  margin-top: 8px;
  text-align: center;
}

.error-feedback {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.label {
  font-weight: 600;
  color: #495057;
  font-size: 12px !important;
}

.mal-text {
  color: #6c757d;
  font-style: italic;
}

.text-danger {
  color: #dc3545;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 0.875rem;
}

.me-1 {
  margin-right: 0.25rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .signature-canvas {
    max-width: 100%;
    height: auto;
  }
  
  .signature-wrapper {
    padding: 10px;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .signature-canvas {
    cursor: default;
  }
}
</style>
